import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  Typography,
  OutlinedInput,
} from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import clientService from 'src/clientService';
import { AcousticIndexEnum, ChartRequest, ChartResponse } from 'src/api/Api';

interface DeploymentActivityTrendChartProps {
  projectDeployments: any[];
  selectedLocation: any;
  selectedDeployment?: any;
}

const DeploymentActivityTrendChart: React.FC<DeploymentActivityTrendChartProps> = ({
  projectDeployments,
  selectedLocation,
  selectedDeployment,
}) => {
  const [chartData, setChartData] = useState<ChartResponse | null>(null);
  const [selectedDeployments, setSelectedDeployments] = useState<string[]>([]);
  const [selectedSpecies, setSelectedSpecies] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Get available species from all deployments' activity trend data
  const availableSpecies = useMemo(() => {
    if (!chartData?.comparison) return [];

    const allSpecies = new Set<string>();
    chartData.comparison.forEach((deployment) => {
      if (deployment.activity_trend?.species_trends) {
        Object.keys(deployment.activity_trend.species_trends).forEach((species) => {
          allSpecies.add(species);
        });
      }
    });

    return Array.from(allSpecies).sort();
  }, [chartData]);

  // Get deployments in the selected location
  const locationDeployments = useMemo(() => {
    return projectDeployments.filter((d) => d.location_id === selectedLocation?.location_id);
  }, [projectDeployments, selectedLocation]);

  const fetchChartData = async (deployments: string[]) => {
    if (deployments.length === 0) {
      setChartData(null);
      return;
    }
    setLoading(true);
    try {
      const req: ChartRequest = {
        deploymentIds: deployments,
        index: [
          AcousticIndexEnum.Aci,
          AcousticIndexEnum.Bi,
          AcousticIndexEnum.Adi,
          AcousticIndexEnum.Aei,
          AcousticIndexEnum.Ndsi,
        ],
      };
      const res =
        await clientService.api.createDeploymentChartsApiV1DeploymentsBioInsituChartPost(req);
      setChartData(res.data);
    } catch (err) {
      console.error('Error fetching deployment chart:', err);
      setChartData(null);
    } finally {
      setLoading(false);
    }
  };

  // Initialize with selected deployment from filter panel, or first deployment if available
  useEffect(() => {
    if (selectedDeployment) {
      // Use the selected deployment from filter panel as the primary selection
      setSelectedDeployments([selectedDeployment.deployment_id]);
    } else if (locationDeployments.length > 0 && selectedDeployments.length === 0) {
      // Fallback to first deployment if no selection from filter panel and no current selection
      const firstDeployment = locationDeployments[0].deployment_id;
      setSelectedDeployments([firstDeployment]);
    }
  }, [selectedDeployment?.deployment_id, locationDeployments.length]);

  // Fetch data when deployments change
  useEffect(() => {
    if (selectedDeployments.length > 0) {
      fetchChartData(selectedDeployments);
    }
  }, [selectedDeployments]);

  // Auto-select first species when species become available
  useEffect(() => {
    if (availableSpecies.length > 0 && selectedSpecies.length === 0) {
      setSelectedSpecies([availableSpecies[0]]);
    }
  }, [availableSpecies, selectedSpecies.length]);

  // Reset species selection when deployments change
  useEffect(() => {
    setSelectedSpecies([]);
  }, [selectedDeployments]);

  const options = useMemo(() => {
    if (!chartData || selectedSpecies.length === 0) return null;

    const series: Highcharts.SeriesOptionsType[] = [];

    selectedSpecies.forEach((species, speciesIndex) => {
      chartData.comparison.forEach((deployment, deploymentIndex) => {
        if (!selectedDeployments.includes(deployment.deployment_id)) return;

        const deploymentInfo = projectDeployments.find(
          (d) => d.deployment_id === deployment.deployment_id,
        );
        const deploymentName = deploymentInfo?.deployment_name || deployment.deployment_id;

        const activityTrend = deployment.activity_trend;
        if (activityTrend?.species_trends?.[species]) {
          const speciesData = activityTrend.species_trends[species];
          // Handle both array format and object with counts property
          let data: number[] = [];
          if (Array.isArray(speciesData)) {
            data = speciesData.map((v: number | null | undefined) => v ?? 0);
          } else if (speciesData && typeof speciesData === 'object' && 'counts' in speciesData) {
            data = (speciesData.counts as number[]).map((v: number | null | undefined) => v ?? 0);
          }

          series.push({
            type: 'line',
            name: `${deploymentName} - ${species}`,
            data: data,
            color: Highcharts.getOptions().colors?.[speciesIndex % 10],
            marker: {
              symbol: 'circle',
              radius: 3,
              lineWidth: 1,
              lineColor: '#ffffff',
            },
          });
        }
      });
    });

    return {
      chart: {
        type: 'line',
        zoomType: 'x',
        height: 400,
      },
      title: {
        text: 'Multi-Deployment Species Activity Trends',
        style: { fontSize: '16px' },
      },
      xAxis: {
        title: { text: 'Time of Day' },
        categories: chartData.time_slots || [],
        labels: { rotation: -45 },
      },
      yAxis: {
        title: { text: 'Activity Level' },
        min: 0,
        labels: {
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
            return (this.value as number).toFixed(1);
          },
        },
      },
      tooltip: {
        pointFormatter: function (this: Highcharts.Point) {
          return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${(
            this.y as number
          ).toFixed(2)}</b><br/>`;
        },
      },
      legend: {
        enabled: true,
      },
      plotOptions: {
        line: {
          marker: {
            radius: 3,
            lineWidth: 1,
            lineColor: '#ffffff',
          },
        },
      },
      series,
    };
  }, [chartData, selectedDeployments, selectedSpecies, projectDeployments]);

  const handleDeploymentChange = (deployments: string[]) => {
    setSelectedDeployments(deployments);
  };

  const handleSpeciesChange = (species: string[]) => {
    setSelectedSpecies(species);
  };

  return (
    <Box sx={{ height: '500px', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {/* Deployment Selection */}
           <Select
             multiple
             value={selectedDeployments}
             onChange={(e) => handleDeploymentChange(e.target.value as string[])}
             input={<OutlinedInput label="Deployments" />}
             renderValue={(selected) => `${selected.length} deployment(s) selected`}
             MenuProps={{
               PaperProps: {
                 style: {
                   maxHeight: 300,
                   maxWidth: 300,
                 },
               },
               anchorOrigin: {
                 vertical: 'bottom',
                 horizontal: 'left',
               },
               transformOrigin: {
                 vertical: 'top',
                 horizontal: 'left',
               },
             }}
             sx={{
              fontSize: '.8rem',
              textAlign: 'center',
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 0,
              p: 0,
              m: 0,
              '& fieldset': { border: 'none' },
              '&:hover': {
                borderColor: 'primary.dark',
              },
              '&.Mui-focused': {
                borderColor: 'primary.main',
                borderBottomWidth: '2px',
              },
              '& .MuiSelect-select': {
                py: 0.5,
              },
            }}
          >
            {locationDeployments.map((d) => (
              <MenuItem key={d.deployment_id} value={d.deployment_id}>
                <Checkbox checked={selectedDeployments.includes(d.deployment_id)} />
                <ListItemText primary={d.deployment_name} />
              </MenuItem>
            ))}
          </Select>
        </Box>
        {/* Species Selection */}
           <Select
             multiple
             value={selectedSpecies}
             onChange={(e) => handleSpeciesChange(e.target.value as string[])}
             input={<OutlinedInput label="Species" />}
             renderValue={(selected) => `${selected.length} species selected`}
             disabled={availableSpecies.length === 0}
             MenuProps={{
               PaperProps: {
                 style: {
                   maxHeight: 300,
                   maxWidth: 300,
                 },
               },
               anchorOrigin: {
                 vertical: 'bottom',
                 horizontal: 'left',
               },
               transformOrigin: {
                 vertical: 'top',
                 horizontal: 'left',
               },
             }}
             sx={{
              fontSize: '.8rem',
              textAlign: 'center',
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 0,
              p: 0,
              m: 0,
              '& fieldset': { border: 'none' },
              '&:hover': {
                borderColor: 'primary.dark',
              },
              '&.Mui-focused': {
                borderColor: 'primary.main',
                borderBottomWidth: '2px',
              },
              '& .MuiSelect-select': {
                py: 0.5,
              },
            }}
          >
            {availableSpecies.map((species) => (
              <MenuItem key={species} value={species}>
                <Checkbox checked={selectedSpecies.includes(species)} />
                <ListItemText primary={species} />
              </MenuItem>
            ))}
          </Select>
      </Box>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '85%',
          }}
        >
          <CircularProgress size={32} />
          <Typography sx={{ ml: 2 }}>Loading activity trends...</Typography>
        </Box>
      ) : !chartData ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '85%',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h6" color="textSecondary">
            No Data Available
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Select deployments and species to view activity trends
          </Typography>
        </Box>
      ) : selectedSpecies.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '85%',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h6" color="textSecondary">
            Select Species
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Choose species from the dropdown to view activity trends
          </Typography>
        </Box>
      ) : (
        <HighchartsReact highcharts={Highcharts} options={options} />
      )}
    </Box>
  );
};

export default DeploymentActivityTrendChart;
